import React, { useEffect, useState } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiClient } from '../config/api';

/**
 * Event study — what typically happens when this firm publishes.
 *
 * Plots the MEDIAN indexed price path across every priced report by a seller,
 * against the all-sellers baseline, by trading day relative to publication.
 *
 * Median, not mean: short outcomes are violently skewed, and one squeeze drags a
 * mean path into nonsense. (Measured on this corpus: Kerrisdale's 1-year median
 * is +20% while its mean is +84%.) The shaded band is p25-p75, so dispersion is
 * visible rather than hidden behind a single false-precision line.
 *
 * Trading-day offsets, not calendar days, so day +20 is the same market age for
 * every report regardless of which holidays its venue keeps.
 */

const SERIES = '#2a78d6';   // this seller
const BASE = '#eb6834';     // all-sellers baseline
const BAND = '#2a78d6';

const StudyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-gray-300 rounded shadow-lg px-3 py-2 text-xs">
      <div className="font-mono font-semibold text-gray-900 mb-1">
        day {label > 0 ? `+${label}` : label}
      </div>
      {row.median != null && (
        <div className="font-mono">
          <span style={{ color: SERIES }}>●</span> this seller{' '}
          <b>{row.median.toFixed(1)}</b>
          <span className="text-gray-400"> n={row.n}</span>
        </div>
      )}
      {row.baseline != null && (
        <div className="font-mono">
          <span style={{ color: BASE }}>●</span> all sellers{' '}
          <b>{row.baseline.toFixed(1)}</b>
        </div>
      )}
      {row.p25 != null && (
        <div className="text-gray-400 font-mono mt-0.5">
          p25–p75 {row.p25.toFixed(1)}–{row.p75.toFixed(1)}
        </div>
      )}
    </div>
  );
};

const EventStudyChart = ({ seller, before = 20, after = 120 }) => {
  const [rows, setRows] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setState('loading');
        // The baseline is the whole point of the chart — a seller's curve means
        // nothing without something to be better or worse than — so both are
        // fetched together and the component renders only when it has both.
        const [mine, all] = await Promise.all([
          apiClient.get('/api/price-impact/event-study', { params: { seller, before, after } }),
          apiClient.get('/api/price-impact/event-study', { params: { before, after } }),
        ]);
        if (cancelled) return;
        const baseByT = new Map(all.data.points.map((p) => [p.t, p.median]));
        const merged = mine.data.points.map((p) => ({
          t: p.t, n: p.n, median: p.median, p25: p.p25, p75: p.p75,
          band: [p.p25, p.p75],
          baseline: baseByT.get(p.t) ?? null,
        }));
        setRows(merged);
        setState(merged.length < 5 ? 'thin' : 'ready');
      } catch (e) {
        if (cancelled) return;
        setState('error');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [seller, before, after]);

  if (state === 'loading') {
    return <div className="animate-pulse"><div className="h-72 bg-gray-100 rounded" /></div>;
  }
  if (state === 'error') {
    return (
      <div className="border border-red-200 bg-red-50 rounded p-4 text-sm text-red-700">
        Could not load the event study.
      </div>
    );
  }
  if (state === 'thin' || !rows?.length) {
    return (
      <div className="border border-dashed border-gray-300 rounded p-6 text-sm text-gray-500">
        <div className="font-medium text-gray-700 mb-1">Not enough priced reports</div>
        This seller needs at least a few targets with price history before a median
        path means anything.
      </div>
    );
  }

  const atZero = rows.find((r) => r.t === 0);
  const last = rows[rows.length - 1];
  const nAt0 = atZero?.n ?? 0;
  const lo = Math.min(...rows.map((r) => r.p25));
  const hi = Math.max(...rows.map((r) => r.p75));

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h3 className="text-base font-semibold text-gray-900">
          What happens after {seller} publishes
        </h3>
        <span className="text-xs font-mono text-gray-500">
          median indexed price · n={nAt0} reports
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Trading days from publication. 100 = the price on the day the report came out.
      </p>

      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 8, right: 16, left: -8, bottom: 4 }}>
            <CartesianGrid stroke="#eceae5" vertical={false} />
            <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']}
                   tickFormatter={(t) => (t > 0 ? `+${t}` : `${t}`)}
                   tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
            <YAxis domain={[Math.floor(lo - 4), Math.ceil(hi + 4)]} width={44}
                   tick={{ fontSize: 10, fill: '#6f6e6a' }} stroke="#c9c8c2" />
            <Tooltip content={<StudyTooltip />} />
            <Legend verticalAlign="top" height={26} iconType="plainline"
                    wrapperStyle={{ fontSize: 12 }} />
            <Area dataKey="band" stroke="none" fill={BAND} fillOpacity={0.10}
                  name="p25–p75" isAnimationActive={false} legendType="none" />
            <ReferenceLine y={100} stroke="#c9c8c2" strokeDasharray="3 3" />
            <ReferenceLine x={0} stroke="#52514e" strokeWidth={1.5}
              label={{ value: 'report', position: 'insideTopRight', fontSize: 10, fill: '#52514e' }} />
            <Line dataKey="baseline" name="All sellers" stroke={BASE} strokeWidth={2}
                  dot={false} isAnimationActive={false} />
            <Line dataKey="median" name={seller} stroke={SERIES} strokeWidth={2}
                  dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        By day +{last.t} the median target sits at{' '}
        <b className={last.median < 100 ? 'text-green-700' : 'text-red-700'}>
          {last.median.toFixed(0)}
        </b>
        {last.baseline != null && <> against {last.baseline.toFixed(0)} for all sellers</>}.
        {' '}Sample thins with horizon — n={last.n} at that point.
      </p>
    </div>
  );
};

export default EventStudyChart;
